import React, { useState, useEffect } from 'react';
import { Minus, Plus, ShoppingCart } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { addToCart } from '../store/slices/cartSlice';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const PastaComponent = () => {
  const dispatch = useDispatch();
  // State to hold pasta items fetched from Firestore.
  const [items, setItems] = useState([]);
  // Local state for tracking quantity for each pasta item (keyed by item id).
  const [quantities, setQuantities] = useState({});

  // useEffect to listen for changes in the Pasta menu category in Firestore.
  useEffect(() => {
    const docRef = doc(db, 'menuCategories', 'Pasta');
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setItems(docSnapshot.data().items || []);
        } else {
          setItems([]);
        }
      },
      (error) => {
        console.error("Error fetching Pasta data:", error);
      }
    );
    return () => unsubscribe();
  }, []);

  /**
   * Updates the quantity of a given pasta item.
   * @param {string} itemId - The unique identifier of the pasta item.
   * @param {number} delta - The change in quantity (+1 or -1).
   */
  const handleQuantityChange = (itemId, delta) => {
    setQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(1, (prev[itemId] || 1) + delta)
    }));
  };

  /**
   * Adds the selected pasta item to the cart.
   * @param {object} item - The pasta item object.
   */
  const handleAddToCart = (item) => {
    const quantity = quantities[item.id] || 1;
    const cartItem = {
      id: `${item.id}-${Date.now()}`,
      name: item.name,
      price: item.price,
      quantity
    };
    dispatch(addToCart(cartItem));
    toast.success(`${item.name} added to cart!`, {
      position: 'top-right',
      autoClose: 3000
    });
  };

  /**
   * Renders a single pasta item card.
   * When item.isAvailable is false, an overlay is rendered (like in the BreakfastComponent).
   * @param {object} item - The pasta item object.
   */
  const renderMenuItem = (item) => {
    const quantity = quantities[item.id] || 1;
    return (
      <div key={item.id} className="bg-black rounded-xl overflow-hidden shadow-lg border border-gray-800 relative max-w-xs">
        <div className="relative">
          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-full h-48 object-cover"
          />
          {/* Overlay for unavailable items */}
          {!item.isAvailable && (
            <div className="absolute inset-0 bg-black bg-opacity-75 flex justify-center items-center">
              <span className="text-red-600 font-bold text-xl">Not Available</span>
            </div>
          )}
          <div className="absolute top-4 right-4">
            <div className="bg-black bg-opacity-50 px-3 py-1 rounded-full">
              <span className="bg-red-500 text-white font-medium text-lg py-1 px-3 rounded-full">
                ₦{item.price.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
        <div className="p-4 space-y-3">
          <h3 className="text-red-500 font-bold text-xl">{item.name}</h3>
          <p className="text-gray-400 text-sm">{item.description}</p>
          <div className="flex flex-row gap-2 pt-2 items-center">
            <div className="flex items-center gap-2 bg-black rounded-lg p-1">
              <button
                onClick={() => handleQuantityChange(item.id, -1)}
                className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                disabled={quantity <= 1}
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-8 text-center font-medium text-red-500">{quantity}</span>
              <button
                onClick={() => handleQuantityChange(item.id, 1)}
                className="p-1 text-gray-300 hover:text-red-500 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => handleAddToCart(item)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              disabled={!item.isAvailable}
            >
              <ShoppingCart className="w-4 h-4" />
              <span>{item.isAvailable ? 'Add to Cart' : 'Not Available'}</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-red-500">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map(item => renderMenuItem(item))}
        </div>
      </div>
    </div>
  );
};

export default PastaComponent;
